/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Devis\ConfigurateurArticle\components\ModalPhotogrammetrie.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useState, useCallback } from 'react';
import { apiFetch } from '../../../../services/httpClient';

interface PhotogrammetrieResult {
  session_id: string;
  dimensions: {
    longueur: number;
    largeur: number;
    hauteur: number;
    volume_approx: number;
  };
  qualite: number;
  nb_points_3d: number;
  nb_correspondances: number;
  photos_traitees: number;
  processing_time: number;
  forme_detectee?: {
    forme: string;
    confidence: number;
    details?: string;
    allow_form_change?: boolean;
  };
}

interface ModalPhotogrammetrieProps {
  isOpen: boolean;
  onClose: () => void;
  onResult: (result: PhotogrammetrieResult) => void;
}

const ModalPhotogrammetrie: React.FC<ModalPhotogrammetrieProps> = ({
  isOpen,
  onClose,
  onResult
}) => {
  const [photos, setPhotos] = useState<File[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [result, setResult] = useState<PhotogrammetrieResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Gestion des fichiers sans react-dropzone
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const validFiles = Array.from(files).filter(file => {
      const isValidType = file.type.includes('image/') || 
                         file.name.toLowerCase().endsWith('.heic') ||
                         file.name.toLowerCase().endsWith('.jpg') ||
                         file.name.toLowerCase().endsWith('.jpeg') ||
                         file.name.toLowerCase().endsWith('.png');
      return isValidType;
    });

    setPhotos(prev => [...prev, ...validFiles]);
    setError(null);
  }, []);

  // Retour conditionnel
  if (!isOpen) return null;

  const removePhoto = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const clearPhotos = () => {
    setPhotos([]);
    setError(null);
  };

  const uploadPhotos = async () => {
    if (photos.length < 5) {
      setError('Minimum 5 photos requises pour une reconstruction 3D');
      return;
    }

    setProcessing(true);
    setError(null);
    setProgress(0);
    setCurrentStep('Upload des photos...');

    try {
      // Étape 1: Upload des photos
      const formData = new FormData();
      photos.forEach(photo => {
        formData.append('photos', photo);
      });

      const uploadData = await apiFetch<any>('/photogrammetrie/upload-heic', {
        method: 'POST',
        body: formData,
        headers: {
        }
      });
      setSessionId(uploadData.session_id);
      setProgress(10);
      setCurrentStep('Photos uploadées, démarrage du traitement...');

      // Étape 2: Lancer le traitement
      await apiFetch(`/photogrammetrie/process/${uploadData.session_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

// Étape 3: Suivre le progrès
      await pollProgress(uploadData.session_id);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      setProcessing(false);
    }
  };

  const pollProgress = async (sessionId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusData = await apiFetch(`/photogrammetrie/status/${sessionId}`, {
          headers: {
          }
        });
        setProgress(statusData.progress);
        setCurrentStep(statusData.current_step);

        if (statusData.status === 'completed') {
          clearInterval(pollInterval);
          setResult(statusData.result);
          setProcessing(false);
          setProgress(100);
          setCurrentStep('Reconstruction 3D terminée !');
        } else if (statusData.status === 'error') {
          clearInterval(pollInterval);
          throw new Error(statusData.error || 'Erreur lors du traitement');
        }
      } catch (err) {
        clearInterval(pollInterval);
        const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
        setError(errorMessage);
        setProcessing(false);
      }
    }, 2000); // Vérifier toutes les 2 secondes
  };

  const useResult = () => {
    if (result) {
      onResult(result);
      onClose();
    }
  };

  const resetModal = () => {
    setPhotos([]);
    setSessionId(null);
    setProcessing(false);
    setProgress(0);
    setCurrentStep('');
    setResult(null);
    setError(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Fonction pour générer la visualisation 3D selon la forme
  const renderVisualization3D = (result: PhotogrammetrieResult) => {
    // Calcul des proportions pour un rendu réaliste
    const maxDim = Math.max(result.dimensions.longueur, result.dimensions.largeur, result.dimensions.hauteur);
    const scaleL = Math.min((result.dimensions.longueur / maxDim) * 200, 200);
    const scaleW = Math.min((result.dimensions.largeur / maxDim) * 120, 120);
    const scaleH = Math.min((result.dimensions.hauteur / maxDim) * 60, 60);
    
    // Déterminer la forme (par défaut rectangulaire_joues)
    const forme = result.forme_detectee?.forme || 'rectangulaire_joues';
    
    // Ombre portée commune
    const shadow = (
      <div 
        className="absolute bg-black rounded-full opacity-10"
        style={{
          width: `${scaleL + 20}px`,
          height: `${scaleW + 10}px`,
          left: '-10px',
          top: `${scaleH + 10}px`,
          transform: 'rotateX(90deg) translateZ(-5px)',
          filter: 'blur(8px)'
        }}
      />
    );
    
    switch (forme) {
      case 'rectangulaire_joues':
      case 'rectangle_joues':
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            {/* Bandeau-plateau (pièce unique incluant dessus + côtés) */}
            {/* Partie dessus */}
            <div 
              className="absolute bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 rounded-lg shadow-lg"
              style={{
                width: `${scaleL - 40}px`,
                height: `${scaleW}px`,
                left: '20px',
                top: '0px',
                transform: 'rotateX(90deg)',
                transformOrigin: 'center top',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-blue-700 opacity-80">BANDEAU-PLATEAU</span>
              </div>
            </div>
            
            {/* Partie côtés du bandeau-plateau (face avant) */}
            <div 
              className="absolute bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 shadow-lg"
              style={{
                width: `${scaleL - 40}px`,
                height: `${scaleH}px`,
                left: '20px',
                top: '0px',
                transform: 'translateZ(0px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-1 bg-gradient-to-br from-blue-50 to-blue-100 opacity-60"></div>
            </div>
            
            {/* Partie côtés du bandeau-plateau (face arrière) */}
            <div 
              className="absolute bg-gradient-to-br from-blue-150 to-blue-250 border-2 border-blue-350"
              style={{
                width: `${scaleL - 40}px`,
                height: `${scaleH}px`,
                left: '20px',
                top: '0px',
                transform: `translateZ(-${scaleW}px)`,
                opacity: 0.7
              }}
            ></div>
            
            {/* Partie côtés du bandeau-plateau (côté droit) */}
            <div 
              className="absolute bg-gradient-to-br from-blue-120 to-blue-220 border-2 border-blue-320"
              style={{
                width: `${scaleW}px`,
                height: `${scaleH}px`,
                left: `${scaleL - 20}px`,
                top: '0px',
                transform: 'rotateY(90deg)',
                transformOrigin: 'left center',
                opacity: 0.8
              }}
            ></div>
            
            {/* Partie côtés du bandeau-plateau (côté gauche) */}
            <div 
              className="absolute bg-gradient-to-br from-blue-120 to-blue-220 border-2 border-blue-320"
              style={{
                width: `${scaleW}px`,
                height: `${scaleH}px`,
                left: '20px',
                top: '0px',
                transform: 'rotateY(-90deg)',
                transformOrigin: 'right center',
                opacity: 0.8
              }}
            ></div>
            
            {/* Fond (dessous) - pièce séparée optionnelle */}
            <div 
              className="absolute bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-300 rounded-lg shadow-lg"
              style={{
                width: `${scaleL - 40}px`,
                height: `${scaleW}px`,
                left: '20px',
                top: '0px',
                transform: `rotateX(90deg) translateZ(-${scaleH}px)`,
                transformOrigin: 'center top',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                opacity: 0.7
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-700 opacity-80">FOND</span>
              </div>
            </div>
            
            {/* Joue 1 (gauche) - pièce séparée */}
            <div 
              className="absolute bg-gradient-to-br from-indigo-100 to-indigo-200 border-2 border-indigo-300 shadow-lg"
              style={{
                width: `${scaleW}px`,
                height: `${scaleH}px`,
                left: '0px',
                top: '0px',
                transform: 'rotateY(-90deg)',
                transformOrigin: 'left center',
                boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-indigo-700 opacity-80">JOUE 1</span>
              </div>
            </div>
            
            {/* Joue 2 (droite) - pièce séparée */}
            <div 
              className="absolute bg-gradient-to-br from-indigo-100 to-indigo-200 border-2 border-indigo-300 shadow-lg"
              style={{
                width: `${scaleW}px`,
                height: `${scaleH}px`,
                left: `${scaleL - 40}px`,
                top: '0px',
                transform: 'rotateY(90deg)',
                transformOrigin: 'right center',
                boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-indigo-700 opacity-80">JOUE 2</span>
              </div>
            </div>
            {shadow}
          </div>
        );
        
      case 'rectangulaire_bandeau':
      case 'rectangle_bandeau':
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            {/* Plateau dessus */}
            <div 
              className="absolute bg-gradient-to-br from-green-100 to-green-200 border-2 border-green-300 rounded-lg shadow-lg"
              style={{
                width: `${scaleL}px`,
                height: `${scaleW}px`,
                left: '0px',
                top: '0px',
                transform: 'rotateX(90deg)',
                transformOrigin: 'center top',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-green-700 opacity-80">PLATEAU DESSUS</span>
              </div>
            </div>
            
            {/* Bandeau périphérique */}
            <div 
              className="absolute bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 shadow-lg"
              style={{
                width: `${scaleL}px`,
                height: `${scaleH}px`,
                left: '0px',
                top: '0px',
                transform: 'translateZ(0px)',
                boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-blue-700 opacity-80">BANDEAU</span>
              </div>
            </div>
            {shadow}
          </div>
        );
        
      case 'rond_bandeau':
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            {/* Plateau dessus circulaire */}
            <div 
              className="absolute bg-gradient-to-br from-green-100 to-green-200 border-2 border-green-300 rounded-full shadow-lg"
              style={{
                width: `${Math.min(scaleL, scaleW)}px`,
                height: `${Math.min(scaleL, scaleW)}px`,
                left: `${(scaleL - Math.min(scaleL, scaleW)) / 2}px`,
                top: '0px',
                transform: 'rotateX(90deg)',
                transformOrigin: 'center top',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-green-700 opacity-80">PLATEAU DESSUS</span>
              </div>
            </div>
            
            {/* Bandeau circulaire */}
            <div 
              className="absolute bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 shadow-lg"
              style={{
                width: `${Math.min(scaleL, scaleW)}px`,
                height: `${scaleH}px`,
                left: `${(scaleL - Math.min(scaleL, scaleW)) / 2}px`,
                top: '0px',
                transform: 'translateZ(0px)',
                borderRadius: `${Math.min(scaleL, scaleW) / 2}px ${Math.min(scaleL, scaleW) / 2}px 0 0`,
                boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-blue-700 opacity-80">BANDEAU</span>
              </div>
            </div>
            {shadow}
          </div>
        );
        
      case 'cylindre_joues':
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            {/* Corps du cylindre */}
            <div 
              className="absolute bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 shadow-lg"
              style={{
                width: `${scaleL - 40}px`,
                height: `${scaleH}px`,
                left: '20px',
                top: '0px',
                transform: 'translateZ(0px)',
                borderRadius: `0 0 ${scaleH / 2}px ${scaleH / 2}px`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-blue-700 opacity-80">CORPS CYLINDRE</span>
              </div>
            </div>
            
            {/* Joue 1 circulaire */}
            <div 
              className="absolute bg-gradient-to-br from-indigo-100 to-indigo-200 border-2 border-indigo-300 rounded-full shadow-lg"
              style={{
                width: `${scaleH}px`,
                height: `${scaleH}px`,
                left: '0px',
                top: '0px',
                transform: 'rotateY(-90deg)',
                transformOrigin: 'left center',
                boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-indigo-700 opacity-80">JOUE 1</span>
              </div>
            </div>
            
            {/* Joue 2 circulaire */}
            <div 
              className="absolute bg-gradient-to-br from-indigo-100 to-indigo-200 border-2 border-indigo-300 rounded-full shadow-lg"
              style={{
                width: `${scaleH}px`,
                height: `${scaleH}px`,
                left: `${scaleL - 20}px`,
                top: '0px',
                transform: 'rotateY(90deg)',
                transformOrigin: 'right center',
                boxShadow: '0 3px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.4)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-indigo-700 opacity-80">JOUE 2</span>
              </div>
            </div>
            {shadow}
          </div>
        );
        
      case 'rectangle_simple':
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            {/* Bâche simple */}
            <div 
              className="absolute bg-gradient-to-br from-yellow-100 to-yellow-200 border-2 border-yellow-300 rounded-lg shadow-lg"
              style={{
                width: `${scaleL}px`,
                height: `${scaleW}px`,
                left: '0px',
                top: '0px',
                transform: 'rotateX(90deg)',
                transformOrigin: 'center top',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-yellow-700 opacity-80">BÂCHE</span>
              </div>
            </div>
            {shadow}
          </div>
        );
        
      case 'rectangle_boite':
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            {/* Plateau complet développé */}
            <div 
              className="absolute bg-gradient-to-br from-purple-100 to-purple-200 border-2 border-purple-300 rounded-lg shadow-lg"
              style={{
                width: `${scaleL}px`,
                height: `${scaleW}px`,
                left: '0px',
                top: '0px',
                transform: 'rotateX(90deg)',
                transformOrigin: 'center top',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-purple-700 opacity-80">PLATEAU COMPLET</span>
              </div>
              {/* Lignes de pliage */}
              <div className="absolute inset-4 border border-dashed border-purple-400 rounded opacity-40"></div>
            </div>
            {shadow}
          </div>
        );
        
      default:
        // Fallback pour forme inconnue
        return (
          <div className="relative" style={{ 
            transform: 'rotateX(15deg) rotateY(-25deg)',
            transformStyle: 'preserve-3d'
          }}>
            <div 
              className="absolute bg-gradient-to-br from-gray-100 to-gray-200 border-2 border-gray-300 rounded-lg shadow-lg"
              style={{
                width: `${scaleL}px`,
                height: `${scaleH}px`,
                left: '0px',
                top: '0px',
                transform: 'translateZ(0px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)'
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-700 opacity-80">FORME INCONNUE</span>
              </div>
            </div>
            {shadow}
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            📸 Reconstruction 3D par photogrammétrie
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Guide d'utilisation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-medium text-blue-900 mb-2">💡 Comment obtenir de meilleurs résultats</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Prenez 20-48 photos sous différents angles</li>
              <li>• Assurez-vous d'un bon éclairage uniforme</li>
              <li>• Évitez les surfaces trop réfléchissantes</li>
              <li>• Gardez l'objet au centre de chaque photo</li>
              <li>• Superposez légèrement les vues entre photos</li>
            </ul>
          </div>

          {/* Zone de sélection de fichiers */}
          {!result && (
            <div className="border-2 border-dashed rounded-lg p-8 text-center border-gray-300 hover:border-gray-400 transition-colors">
              <div className="space-y-4">
                <div className="text-4xl">📷</div>
                <div>
                  <p className="text-lg font-medium text-gray-900">
                    Sélectionnez vos photos
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Formats acceptés: HEIC, JPG, JPEG, PNG
                  </p>
                  <p className="text-sm text-blue-600 font-medium">
                    Minimum 5 photos • Recommandé: 20-48 photos pour une qualité optimale
                  </p>
                </div>
                <div className="mt-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*,.heic"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Choisir les photos
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Liste des photos */}
          {photos.length > 0 && !result && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-medium text-gray-900">
                  Photos sélectionnées ({photos.length})
                </h3>
                <button
                  onClick={clearPhotos}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Tout supprimer
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-40 overflow-y-auto">
                {photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <div className="aspect-square bg-gray-200 rounded border flex items-center justify-center text-xs text-gray-600 p-1">
                      <span className="truncate">{photo.name}</span>
                    </div>
                    <button
                      onClick={() => removePhoto(index)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bouton de traitement */}
          {!result && (
            <div className="text-center">
              <button
                onClick={uploadPhotos}
                disabled={photos.length < 5 || processing}
                className={`px-6 py-3 rounded-lg font-medium ${
                  photos.length < 5 || processing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {processing ? 'Traitement en cours...' : 'Lancer la reconstruction 3D'}
              </button>
              
              {photos.length > 0 && photos.length < 5 && (
                <p className="text-sm text-red-600 mt-2">
                  {5 - photos.length} photo(s) supplémentaire(s) requise(s)
                </p>
              )}
            </div>
          )}

          {/* Barre de progression */}
          {processing && (
            <div className="bg-white border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900">Progression</span>
                <span className="text-sm text-gray-600">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">{currentStep}</p>
            </div>
          )}

          {/* Erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="text-red-400 mr-3">⚠️</div>
                <div>
                  <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Résultats */}
          {result && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="text-green-400 mr-3 text-2xl">✅</div>
                <div>
                  <h3 className="text-lg font-medium text-green-800">
                    Reconstruction 3D réussie !
                  </h3>
                  <p className="text-sm text-green-700">
                    Temps de traitement: {result.processing_time.toFixed(1)}s
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">📐 Dimensions détectées</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Longueur (cm)</label>
                      <input
                        type="number"
                        value={result.dimensions.longueur.toFixed(1)}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          if (!isNaN(newValue)) {
                            setResult(prev => prev ? {
                              ...prev,
                              dimensions: { ...prev.dimensions, longueur: newValue }
                            } : null);
                          }
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="1"
                        max="500"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Largeur (cm)</label>
                      <input
                        type="number"
                        value={result.dimensions.largeur.toFixed(1)}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          if (!isNaN(newValue)) {
                            setResult(prev => prev ? {
                              ...prev,
                              dimensions: { ...prev.dimensions, largeur: newValue }
                            } : null);
                          }
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="1"
                        max="500"
                        step="0.1"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Hauteur (cm)</label>
                      <input
                        type="number"
                        value={result.dimensions.hauteur.toFixed(1)}
                        onChange={(e) => {
                          const newValue = parseFloat(e.target.value);
                          if (!isNaN(newValue)) {
                            setResult(prev => prev ? {
                              ...prev,
                              dimensions: { ...prev.dimensions, hauteur: newValue }
                            } : null);
                          }
                        }}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        min="1"
                        max="100"
                        step="0.1"
                      />
                    </div>
                    <div className="pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-600">
                        Volume: <span className="font-medium">{(result.dimensions.longueur * result.dimensions.largeur * result.dimensions.hauteur / 1000000).toFixed(3)} m³</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">📊 Qualité reconstruction</h4>
                  <div className="space-y-1 text-sm mb-4">
                    <p>Qualité: <span className="font-medium">{(result.qualite * 100).toFixed(0)}%</span></p>
                    <p>Points 3D: <span className="font-medium">{result.nb_points_3d.toLocaleString()}</span></p>
                    <p>Correspondances: <span className="font-medium">{result.nb_correspondances.toLocaleString()}</span></p>
                    <p>Photos traitées: <span className="font-medium">{result.photos_traitees}</span></p>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <h5 className="font-medium text-gray-900 mb-3">🎯 Vue 3D reconstruite</h5>
                    <div className="flex flex-col items-center space-y-3">
                      <div className="w-full h-64 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-6 shadow-inner">
                        {/* Vue 3D réaliste avec CSS 3D */}
                        <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: '800px' }}>
                          {renderVisualization3D(result)}
                          
                          {/* Dimensions flottantes */}
                          <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-slate-200">
                            <div className="text-xs font-bold text-slate-700">
                              {result.dimensions.longueur.toFixed(0)} × {result.dimensions.largeur.toFixed(0)} × {result.dimensions.hauteur.toFixed(0)} cm
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              Volume: {(result.dimensions.longueur * result.dimensions.largeur * result.dimensions.hauteur / 1000000).toFixed(3)} m³
                            </div>
                          </div>
                          
                          {/* Indicateur de forme */}
                          <div className="absolute bottom-2 left-2 bg-green-100/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg border border-green-200">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs font-semibold text-green-700">
                                {result.forme_detectee?.forme === 'rectangulaire_joues' ? 'Rectangle avec joues' :
                                 result.forme_detectee?.forme === 'rectangulaire_bandeau' ? 'Rectangle avec bandeau' :
                                 result.forme_detectee?.forme === 'rond_bandeau' ? 'Rond avec bandeau' :
                                 result.forme_detectee?.forme === 'cylindre_joues' ? 'Cylindre avec joues' :
                                 result.forme_detectee?.forme === 'rectangle_simple' ? 'Bâche simple' :
                                 result.forme_detectee?.forme === 'rectangle_boite' ? 'Rectangle boîte' :
                                 'Forme détectée'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-center text-gray-700">
                        <strong>Vue 3D du coussin reconstruit</strong><br />
                        <span className="text-xs text-gray-500">Basée sur vos photos - Forme détectée automatiquement</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-800">
                      💡 <strong>Conseil:</strong> Vous pouvez ajuster les dimensions ci-contre si elles ne semblent pas correctes.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {result ? 'Annuler' : 'Fermer'}
          </button>
          
          {result && (
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  // Utiliser les dimensions mais permettre de modifier la forme
                  if (result) {
                    onResult({
                      ...result,
                      forme_detectee: { 
                        forme: result.forme_detectee?.forme || 'rectangulaire_joues',
                        confidence: result.forme_detectee?.confidence || 0.5,
                        details: result.forme_detectee?.details,
                        allow_form_change: true 
                      }
                    });
                    onClose();
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
              >
                Modifier la forme
              </button>
              <button
                onClick={useResult}
                className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors"
              >
                Utiliser ces dimensions
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalPhotogrammetrie;